import { Box, Grid, ListItemIcon, ListItemText, MenuItem, MenuList, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState, forwardRef } from "react";
import useSideBar from "../utils/useSideBar";
import useQueryParams from "src/hooks/useQueryParams";
import useAuthGlobal from "src/context/useAuthGlobal";
import appConfig from "src/config/env";
import { theme } from "src/config/theme";
import useAppGlobal from "src/context/useAppGlobal";

import { Link, useNavigate } from "react-router-dom";

const LinkMenuItem = forwardRef(function LinkMenuItem({ to, children, disabled, ...props }, ref) {
  const { handleCloseSidebar } = useAppGlobal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleClick = () => {
    if (disabled) return;
    if (isMobile) {
      handleCloseSidebar();
    }
    navigate(to);
  };

  return (
    <MenuItem
      ref={ref}
      onClick={handleClick}
      disabled={disabled}
      {...props}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <Link
        to={to}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (disabled) e.preventDefault();
        }}
        style={{
          textDecoration: "none",
          color: "inherit",
          display: "flex",
          alignItems: "center",
          width: "100%",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {children}
      </Link>
    </MenuItem>
  );
});

export default function SideBar() {
  const { userMe } = useAuthGlobal();
  const { sidebarOpen } = useAppGlobal();

  const [option] = useQueryParams();
  const sidebar = useSideBar();

  const sidebarRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);

  useEffect(() => {
    if (!sidebarRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setSidebarWidth(entry.contentRect.width);
      }
    });

    observer.observe(sidebarRef.current);
    return () => observer.disconnect();
  }, []);

  const isExpanded = sidebarWidth > 56;

  return (
    <Grid
      ref={sidebarRef}
      container
      sx={{
        height: "100vh",
        overflow: "auto",
        background: "var(--black)",
        width: { lg: "260px", md: "56px", xs: sidebarOpen ? "100%" : "0" },
        position: { xs: sidebarOpen ? "fixed" : undefined },
        zIndex: { xs: sidebarOpen ? 100 : 0 },
        transition: "width 0.2s ease",
        "@media (min-width: 765px)": {
          "&:hover": { width: "260px" },
        },
      }}
    >
      <Grid
        item
        xs={12}
      >
        {/* User Info */}
        <MenuItem sx={{ display: { lg: "none", md: "flex" } }}>
          <Box
            display="flex"
            gap={2}
          >
            <Box
              sx={{
                width: "23px",
                height: "23px",
                background: `url(${appConfig.API_FILE}/${userMe?.avatar?.src}) center/cover no-repeat`,
                borderRadius: "100px",
              }}
            />
            <Box>
              <Typography
                fontWeight="bold"
                sx={{
                  display: !isExpanded ? { xs: "none", sm: "none", lg: "block" } : "block",
                }}
                color={theme.palette.background.default}
              >
                {userMe?.fullName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  display: !isExpanded ? { xs: "none", sm: "none", lg: "block" } : "block",
                }}
                color={theme.palette.background.default}
              >
                {userMe?.role?.name}
              </Typography>
            </Box>
          </Box>
        </MenuItem>

        {/* Sidebar Menu */}
        <MenuList sx={{ pb: "60px" }}>
          {sidebar.flatMap((v) => {
            // Filter children by display
            const visibleChildren = v.child?.filter((child) => child.display !== false) ?? [];

            // Hide parent if it only has hidden children
            if (v.child?.length && visibleChildren.length === 0) {
              return [];
            }

            const items = [
              <LinkMenuItem
                key={`menuItem-${v.id}`}
                to={v.path}
              >
                {!!v.icon && <ListItemIcon>{v.icon}</ListItemIcon>}

                <ListItemText
                  sx={{
                    display: !isExpanded ? { xs: "none", sm: "none", lg: "block" } : "block",
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    variant="subtitle1"
                    sx={{
                      color: "var(--white)",
                      whiteSpace: "normal",
                    }}
                  >
                    {v.title}
                  </Typography>
                </ListItemText>
              </LinkMenuItem>,
            ];

            if (visibleChildren.length) {
              items.push(
                <MenuList key={`menuList-${v.id}`}>
                  {visibleChildren.map((val) => (
                    <LinkMenuItem
                      key={val.id}
                      to={val.path}
                      disabled={val.disabled}
                      sx={{ p: "0 !important" }}
                    >
                      {!!val.icon && <ListItemIcon>{val.icon}</ListItemIcon>}

                      <ListItemText
                        sx={{
                          p: "6px 0",
                          background: val.name === option?.tab ? "var(--white)" : "var(--black)",
                          color: "var(--black)",
                          display: !isExpanded ? { xs: "none", sm: "none", lg: "block" } : "block",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            ml: "36px",
                            color: val.name === option?.tab ? "var(--black)" : "var(--white)",
                            whiteSpace: "normal",
                          }}
                        >
                          {val.title}
                        </Typography>
                      </ListItemText>
                    </LinkMenuItem>
                  ))}
                </MenuList>,
              );
            }

            return items;
          })}
        </MenuList>
      </Grid>
    </Grid>
  );
}
